import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Alert,
  Chip,
  Autocomplete,
  CircularProgress,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { brandApi, statsApi } from '../../services/api';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

// Custom hook for debouncing a value
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

const STATUS_META = {
  unmapped: { label: 'Unmapped', color: 'warning' },
  mapped: { label: 'Mapped', color: 'success' },
  normalized_no_rule: { label: 'No rule', color: 'error' },
};

// Retailer count with the full list on hover - brands span up to 7 retailers,
// so inline chips would make every row tall.
function RetailerCell({ retailers, count }) {
  if (!count) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Tooltip
      arrow
      title={
        <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
          {retailers.join('\n')}
        </Box>
      }
    >
      <Chip label={count} size="small" variant="outlined" sx={{ cursor: 'help' }} />
    </Tooltip>
  );
}

export default function BrandMappingManager() {
  // Unified list state
  const [brands, setBrands] = useState([]);
  const [counts, setCounts] = useState({ all: 0, unmapped: 0, mapped: 0, normalized_no_rule: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [normalizedBrands, setNormalizedBrands] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters, sorting, pagination
  const [statusFilter, setStatusFilter] = useState('');
  const [retailerFilter, setRetailerFilter] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [sortBy, setSortBy] = useState('products');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Mapping state for each row
  const [mappingSelections, setMappingSelections] = useState({});
  const [savingRows, setSavingRows] = useState({});

  // Batch mapping dialog
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [batchNormalizedBrand, setBatchNormalizedBrand] = useState('');
  const [batchSaving, setBatchSaving] = useState(false);

  // Edit existing mapping state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editNewNormalizedValue, setEditNewNormalizedValue] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Orphan backfill dialog
  const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);
  const [orphans, setOrphans] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const loadBrands = useCallback(async () => {
    setLoading(true);
    try {
      const data = await brandApi.getAllBrands({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        search: debouncedSearch,
        status: statusFilter,
        retailer_id: retailerFilter,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      setBrands(data.brands || []);
      setTotalCount(data.total || 0);
      setCounts(data.counts || { all: 0, unmapped: 0, mapped: 0, normalized_no_rule: 0 });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter, retailerFilter, sortBy, sortDir]);

  const loadNormalizedBrands = useCallback(async () => {
    try {
      const data = await brandApi.getNormalizedBrands();
      setNormalizedBrands(data || []);
    } catch (err) {
      console.error('Failed to load normalized brands:', err);
    }
  }, []);

  useEffect(() => {
    loadNormalizedBrands();
    statsApi.getRetailers().then(setRetailers).catch(() => {});
  }, [loadNormalizedBrands]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // Any filter change invalidates the current page offset
  const resetPage = () => setPage(0);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir(field === 'products' || field === 'retailers' ? 'desc' : 'asc');
    }
    resetPage();
  };

  const handleMappingChange = (rawBrand, normalizedBrand) => {
    setMappingSelections((prev) => ({
      ...prev,
      [rawBrand]: normalizedBrand,
    }));
  };

  const handleSaveMapping = async (rawBrand) => {
    const normalizedBrand = mappingSelections[rawBrand];
    if (!normalizedBrand) {
      setError('Please select or enter a normalized brand');
      return;
    }

    setSavingRows((prev) => ({ ...prev, [rawBrand]: true }));
    setError(null);

    try {
      const result = await brandApi.createMapping(rawBrand, normalizedBrand);
      if (result.success) {
        setSuccess(`Mapped "${rawBrand}" to "${normalizedBrand}" (${result.affected_products} products)`);
        setMappingSelections((prev) => {
          const newSelections = { ...prev };
          delete newSelections[rawBrand];
          return newSelections;
        });
        loadNormalizedBrands();
        loadBrands();
      } else {
        setError(result.error || 'Failed to create mapping');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingRows((prev) => ({ ...prev, [rawBrand]: false }));
    }
  };

  const handleToggleSelect = (rawBrand) => {
    setSelectedBrands((prev) =>
      prev.includes(rawBrand)
        ? prev.filter((b) => b !== rawBrand)
        : [...prev, rawBrand]
    );
  };

  // Only unmapped rows are batch-mappable; mapped rows already have a rule.
  const selectableBrands = useMemo(
    () => brands.filter((b) => b.status !== 'mapped').map((b) => b.raw_brand),
    [brands]
  );

  const handleSelectAll = () => {
    const allSelected =
      selectableBrands.length > 0 && selectableBrands.every((b) => selectedBrands.includes(b));
    setSelectedBrands(allSelected ? [] : selectableBrands);
  };

  const handleBatchMap = async () => {
    if (!batchNormalizedBrand || selectedBrands.length === 0) {
      setError('Please select brands and enter a normalized brand name');
      return;
    }

    setBatchSaving(true);
    setError(null);

    try {
      const mappings = selectedBrands.map((rawBrand) => ({
        raw_brand: rawBrand,
        normalized_brand: batchNormalizedBrand,
      }));

      const result = await brandApi.batchCreateMappings(mappings);
      if (result.success || result.created > 0) {
        setSuccess(`Created ${result.created} brand mappings`);
        setBatchDialogOpen(false);
        setSelectedBrands([]);
        setBatchNormalizedBrand('');
        loadBrands();
        loadNormalizedBrands();
      }

      if (result.errors && result.errors.length > 0) {
        setError(`Some mappings failed: ${result.errors.map((e) => e.error).join(', ')}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBatchSaving(false);
    }
  };

  const handleOpenEditMapping = (record) => {
    setEditRecord(record);
    setEditNewNormalizedValue('');
    setEditDialogOpen(true);
  };

  const handleSaveEditMapping = async () => {
    if (!editRecord || !editNewNormalizedValue) return;

    setActionLoading(true);
    try {
      const result = await brandApi.updateMapping(
        editRecord.mapping_id,
        editRecord.raw_brand,
        editNewNormalizedValue
      );

      if (result.success) {
        setSuccess(
          `Updated mapping: "${editRecord.raw_brand}" → "${editNewNormalizedValue}" (${result.products_updated} products updated)`
        );
        setEditDialogOpen(false);
        setEditRecord(null);
        setEditNewNormalizedValue('');
        loadBrands();
        loadNormalizedBrands();
      } else {
        setError(result.error || 'Failed to update mapping');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenOrphans = async () => {
    setOrphanDialogOpen(true);
    setOrphanLoading(true);
    try {
      setOrphans(await brandApi.getOrphanBrands());
    } catch (err) {
      setError(err.message);
      setOrphanDialogOpen(false);
    } finally {
      setOrphanLoading(false);
    }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const result = await brandApi.backfillOrphanBrands();
      if (result.success) {
        setSuccess(`Restored ${result.restored} brand mapping rule(s)`);
        setOrphanDialogOpen(false);
        setOrphans(null);
        loadBrands();
        loadNormalizedBrands();
      } else {
        setError(result.error || 'Backfill failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBackfilling(false);
    }
  };

  const normalizedBrandOptions = normalizedBrands.map((b) => b.normalized_brand);

  const sortHeader = (field, label, align) => (
    <TableCell align={align} sortDirection={sortBy === field ? sortDir : false}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Brand Mapping Manager
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Map raw brand values to normalized brand names. Products with unmapped brands won't be matched correctly.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {counts.normalized_no_rule > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          icon={<WarningIcon fontSize="inherit" />}
          action={
            <Button color="inherit" size="small" onClick={handleOpenOrphans}>
              Review
            </Button>
          }
        >
          {counts.normalized_no_rule} brand(s) have a normalized value with no mapping rule. They
          will lose their brand if products are re-normalized.
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Search brand"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetPage();
              }}
              placeholder="Raw or normalized value..."
              sx={{ flexGrow: 1, minWidth: 220 }}
            />

            <FormControl size="small" sx={{ minWidth: 210 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  resetPage();
                }}
              >
                <MenuItem value="">All ({counts.all})</MenuItem>
                <MenuItem value="unmapped">Unmapped ({counts.unmapped})</MenuItem>
                <MenuItem value="mapped">Existing mappings ({counts.mapped})</MenuItem>
                <MenuItem value="normalized_no_rule">
                  Normalized, no rule ({counts.normalized_no_rule})
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Retailer</InputLabel>
              <Select
                value={retailerFilter}
                label="Retailer"
                onChange={(e) => {
                  setRetailerFilter(e.target.value);
                  resetPage();
                }}
              >
                <MenuItem value="">All retailers</MenuItem>
                {retailers.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name || r.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={selectedBrands.length === 0}
              onClick={() => setBatchDialogOpen(true)}
            >
              Batch Map ({selectedBrands.length})
            </Button>

            <Tooltip title="Refresh">
              <IconButton onClick={loadBrands} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Unified Brands Table */}
      <Card>
        <CardContent>
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
            <Chip label={`${counts.all} total`} size="small" variant="outlined" />
            <Chip label={`${counts.unmapped} unmapped`} size="small" color="warning" variant="outlined" />
            <Chip label={`${counts.mapped} mapped`} size="small" color="success" variant="outlined" />
            {counts.normalized_no_rule > 0 && (
              <Chip
                label={`${counts.normalized_no_rule} no rule`}
                size="small"
                color="error"
                variant="outlined"
              />
            )}
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={
                        selectableBrands.length > 0 &&
                        selectableBrands.every((b) => selectedBrands.includes(b))
                      }
                      onChange={handleSelectAll}
                      disabled={selectableBrands.length === 0}
                    />
                  </TableCell>
                  {sortHeader('raw_brand', 'Raw Brand')}
                  {sortHeader('normalized_value', 'Normalized Value')}
                  <TableCell>Status</TableCell>
                  {sortHeader('products', 'Products', 'right')}
                  {sortHeader('retailers', 'Retailers', 'center')}
                  <TableCell sx={{ minWidth: { xs: 150, sm: 260 } }}>Map To</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brands.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No brands match these filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  brands.map((brand) => {
                    const meta = STATUS_META[brand.status] || {};
                    const isMapped = brand.status === 'mapped';
                    return (
                      <TableRow key={brand.raw_brand} hover>
                        <TableCell padding="checkbox">
                          <input
                            type="checkbox"
                            checked={selectedBrands.includes(brand.raw_brand)}
                            onChange={() => handleToggleSelect(brand.raw_brand)}
                            disabled={isMapped}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {brand.raw_brand}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {brand.normalized_value ? (
                            <Chip
                              label={brand.normalized_value}
                              size="small"
                              color={isMapped ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={meta.label} size="small" color={meta.color} />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={brand.product_count.toLocaleString()}
                            size="small"
                            color={brand.product_count > 100 ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <RetailerCell retailers={brand.retailers} count={brand.retailer_count} />
                        </TableCell>
                        <TableCell>
                          {isMapped ? (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            <Autocomplete
                              freeSolo
                              size="small"
                              options={normalizedBrandOptions}
                              value={mappingSelections[brand.raw_brand] || ''}
                              onChange={(event, newValue) =>
                                handleMappingChange(brand.raw_brand, newValue)
                              }
                              onInputChange={(event, newInputValue) =>
                                handleMappingChange(brand.raw_brand, newInputValue)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select or type new brand..."
                                  size="small"
                                />
                              )}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {isMapped ? (
                            <Tooltip title="Edit mapping">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditMapping(brand)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Save mapping">
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleSaveMapping(brand.raw_brand)}
                                  disabled={
                                    !mappingSelections[brand.raw_brand] ||
                                    savingRows[brand.raw_brand]
                                  }
                                >
                                  {savingRows[brand.raw_brand] ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <CheckIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      {/* Batch Mapping Dialog */}
      <Dialog open={batchDialogOpen} onClose={() => setBatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Batch Map Brands</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Map {selectedBrands.length} selected brand(s) to a single normalized brand:
          </Typography>
          <Box sx={{ mb: 2 }}>
            {selectedBrands.map((brand) => (
              <Chip
                key={brand}
                label={brand}
                size="small"
                sx={{ mr: 0.5, mb: 0.5 }}
                onDelete={() => handleToggleSelect(brand)}
              />
            ))}
          </Box>
          <Autocomplete
            freeSolo
            options={normalizedBrandOptions}
            value={batchNormalizedBrand}
            onChange={(event, newValue) => setBatchNormalizedBrand(newValue || '')}
            onInputChange={(event, newInputValue) => setBatchNormalizedBrand(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Normalized Brand"
                placeholder="Select existing or type new..."
                fullWidth
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBatchMap}
            disabled={!batchNormalizedBrand || selectedBrands.length === 0 || batchSaving}
          >
            {batchSaving ? <CircularProgress size={20} /> : 'Map All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Brand Mapping Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditRecord(null);
          setEditNewNormalizedValue('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Brand Mapping</DialogTitle>
        <DialogContent>
          {editRecord && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Raw Brand: <strong>{editRecord.raw_brand}</strong>
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Current Normalized Brand:{' '}
                <Chip
                  label={editRecord.normalized_value}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Autocomplete
                freeSolo
                options={normalizedBrandOptions.filter(
                  (opt) => opt !== editRecord.normalized_value
                )}
                value={editNewNormalizedValue}
                onChange={(event, newValue) => setEditNewNormalizedValue(newValue || '')}
                onInputChange={(event, newInputValue) => setEditNewNormalizedValue(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="New Normalized Brand"
                    placeholder="Select existing or type new brand..."
                    fullWidth
                  />
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setEditRecord(null);
              setEditNewNormalizedValue('');
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEditMapping}
            disabled={!editNewNormalizedValue || actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Orphan Backfill Dialog */}
      <Dialog
        open={orphanDialogOpen}
        onClose={() => setOrphanDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Brands with no mapping rule</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            These products carry a normalized brand that no mapping rule produces. Backfilling
            re-adds each raw value to the rule that already produces its value — no product's brand
            changes. Inactive products are included, so this list can be longer than the count
            above, which only covers active products.
          </Typography>

          {orphanLoading && <LinearProgress sx={{ mb: 2 }} />}

          {orphans && (
            <>
              {orphans.unresolvable_count > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {orphans.unresolvable_count} brand(s) point at a normalized value that no longer
                  exists and need to be mapped by hand.
                </Alert>
              )}
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Raw Brand</TableCell>
                      <TableCell>Normalized Value</TableCell>
                      <TableCell align="right">Products</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orphans.backfillable.map((o) => (
                      <TableRow key={o.raw_brand}>
                        <TableCell>{o.raw_brand}</TableCell>
                        <TableCell>
                          <Chip label={o.normalized_brand} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{o.product_count.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrphanDialogOpen(false)} disabled={backfilling}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBackfill}
            disabled={backfilling || !orphans || orphans.backfillable_count === 0}
          >
            {backfilling ? (
              <CircularProgress size={20} />
            ) : (
              `Backfill ${orphans ? orphans.backfillable_count : 0} rule(s)`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
